import { useEffect, useState } from 'react';
import type { FreebeManualFileInspection, FreebeManualImportFamily } from '@tosho/contracts';
import { freebeIntegrationApi, manualImportErrorMessage } from './freebe-integration-api';

/** A selected file is inspected without importing it. Every import is still explicit.
 * Cleanup prevents a slow response for an earlier file from relabelling its replacement. */
export function FreebeManualImportAction({
  file,
  family,
  csrfToken,
  disabled,
  onImport,
}: {
  readonly file: File;
  readonly family: FreebeManualImportFamily;
  readonly csrfToken: string;
  readonly disabled: boolean;
  readonly onImport: () => Promise<void>;
}): React.JSX.Element {
  const [inspection, setInspection] = useState<{
    file: File;
    value: FreebeManualFileInspection;
  } | null>(null);
  const [failure, setFailure] = useState<{ file: File; message: string } | null>(null);
  const [importing, setImporting] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setInspection(null);
    setFailure(null);
    const extension = family === 'quotes' || family === 'movements' ? '.xlsx' : '.json';
    if (!file.size || file.size > 15_728_640 || !file.name.toLowerCase().endsWith(extension)) {
      setFailure({ file, message: `Choisissez un export ${extension} de 15 Mo maximum.` });
      return () => {
        active = false;
        controller.abort();
      };
    }
    void freebeIntegrationApi
      .inspectManualFile(family, file, csrfToken, controller.signal)
      .then((value) => {
        if (active) setInspection({ file, value });
      })
      .catch((error: unknown) => {
        if (active) setFailure({ file, message: manualImportErrorMessage(error, 'inspection') });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [file, family, csrfToken, revision]);
  const current = inspection?.file === file ? inspection.value : null;
  const error = failure?.file === file ? failure.message : null;
  const ready = current?.status === 'references_checked';
  const annual = current?.period.kind === 'CURRENT_YEAR';
  const dates = current?.period;
  const years =
    dates?.firstDate && dates.lastDate
      ? dates.firstDate.slice(0, 4) === dates.lastDate.slice(0, 4)
        ? dates.firstDate.slice(0, 4)
        : `${dates.firstDate.slice(0, 4)}–${dates.lastDate.slice(0, 4)}`
      : null;
  return (
    <span className="freebe-integration__manual-action" aria-live="polite">
      {error ? (
        <small role="alert">{error}</small>
      ) : !current ? (
        <small>Analyse du fichier…</small>
      ) : current.status !== 'references_checked' ? (
        <small role="alert">
          {current.status === 'blocked'
            ? 'Ce fichier ne peut pas être importé dans cette carte.'
            : 'Ce fichier nécessite une vérification de ses références. Aucun import effectué.'}
        </small>
      ) : (
        <small>
          {annual
            ? `Fichier MAJ ${years}`
            : years
              ? `Historique ${years}`
              : dates?.kind === 'UNDETERMINED'
                ? 'Période non déterminée · contrôle comme historique complet'
                : 'Liste complète · période non fournie par Freebe'}
          {` · ${current.rowCount} ligne(s)`}
          {annual ? ' · Les années précédentes sont conservées.' : ''}
        </small>
      )}
      {error && !current ? (
        <button
          type="button"
          className="is-secondary"
          disabled={disabled}
          onClick={() => setRevision((value) => value + 1)}
        >
          Réanalyser le fichier
        </button>
      ) : null}
      <button
        type="button"
        disabled={disabled || importing || !ready}
        onClick={() => {
          if (importing || !ready) return;
          setImporting(true);
          setFailure(null);
          void onImport()
            .catch((caught: unknown) => {
              setFailure({ file, message: manualImportErrorMessage(caught) });
            })
            .finally(() => setImporting(false));
        }}
      >
        {importing ? 'Import en cours…' : annual ? 'Import fichier MAJ' : 'Importer l’historique'}
      </button>
    </span>
  );
}
